import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ModalComponent } from '../../ui/modal-component/modal-component.component';

/** Verbs that lose or stop something. Matched against the first word of the button. */
const DANGER_VERBS = new Set([
  'delete',
  'remove',
  'cancel',
  'archive',
  'disable',
  'deactivate',
  'blacklist',
  'block',
  'stop',
  'revoke',
  'discard',
  'unsubscribe',
]);

let nextTitleId = 0;

/**
 * Confirmation dialog. The safe choice (Close) sits first and takes focus;
 * the action sits last. Actions that lose or stop something are drawn in the
 * failure colour, and `impact` (or an element marked `impact`) states what is
 * at stake, e.g. "Goes out in 2 h 14 m to 1,240 recipients, 2 parts each".
 */
@Component({
  selector: 'app-prompt-card',
  standalone: true,
  imports: [ModalComponent],
  templateUrl: './prompt-card.component.html',
  styleUrl: './prompt-card.component.css',
})
export class PromptCardComponent {
  @Input({ required: true }) openModal = false;
  @Input() buttonText = '';
  @Input() title = '';
  @Output() onClose = new EventEmitter();
  @Output() onButtonClick = new EventEmitter();
  @Input() loading: boolean = false;
  /** Disables the action while the person still has something to do (e.g. no recipients). */
  @Input() actionDisabled = false;
  @Input() leftButtonCLass: string = '';
  @Input() rightButtonCLass: string = '';

  /** One line on what is at stake, shown above the buttons. */
  @Input() impact: string = '';
  /** Material Symbols name for the impact line. */
  @Input() impactIcon: string = 'info';
  /** 'auto' reads the action verb; force 'danger' or 'default' when it guesses wrong. */
  @Input() tone: 'auto' | 'danger' | 'default' = 'auto';
  /**
   * Label of the button that closes without acting. Defaults to 'Close', or
   * 'Keep it' when the action itself is 'Cancel', so the two never read alike.
   */
  @Input() closeText: string = '';

  private readonly uid = nextTitleId++;
  readonly titleId = `prompt-title-${this.uid}`;
  readonly bodyId = `prompt-body-${this.uid}`;
  readonly impactId = `prompt-impact-${this.uid}`;
  readonly impactSlotId = `prompt-impact-slot-${this.uid}`;

  /** The body and the impact line, read out after the title when the dialog opens. */
  get describedBy(): string {
    return [this.bodyId, this.impact ? this.impactId : '', this.impactSlotId].filter(Boolean).join(' ');
  }

  private get verb(): string {
    return (this.buttonText || '').trim().split(/\s+/)[0].toLowerCase();
  }

  get isDanger(): boolean {
    if (this.tone !== 'auto') return this.tone === 'danger';
    // Consumers that asked for a red button through a class keep getting one.
    return DANGER_VERBS.has(this.verb) || /\b(bg-danger|bg-red-\d+)\b/.test(this.rightButtonCLass || '');
  }

  get closeLabel(): string {
    if (this.closeText) return this.closeText;
    return this.verb === 'cancel' ? 'Keep it' : 'Close';
  }

  /** "delete" → "Delete": sentence case without touching the rest. */
  get actionLabel(): string {
    const text = (this.buttonText || '').trim();
    return text ? text[0].toUpperCase() + text.slice(1) : 'Confirm';
  }
}
